import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  App,
  Button,
  Input,
  Modal,
  Spin,
  Typography,
} from "antd";
import {
  FaAddressBook,
  FaBook,
  FaBolt,
  FaBullhorn,
  FaCalendarAlt,
  FaChartLine,
  FaCheckCircle,
  FaClock,
  FaComments,
  FaCopy,
  FaDatabase,
  FaEye,
  FaFileAlt,
  FaInbox,
  FaLink,
  FaPaperPlane,
  FaPlus,
  FaUserFriends,
  FaUserMinus,
  FaUserPlus,
  FaWhatsapp,
} from "react-icons/fa";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useNavigate } from "react-router-dom";
import { businessPath } from "../../config/businessMenu";
import { apihubGet } from "../../api/apihub";
import {
  fetchLatestCampaignSummary,
  fetchMetaPhoneStatus,
  fetchSentMessagesByType,
  loadDashboardSession,
} from "../../api/dashboard.api";
import { fetchCampaignList } from "../../api/campaigns.api";
import { useProjectPhone } from "../../hooks/useProjectPhone";
import { useDashboardGate } from "./DashboardGateContext";
import { useTheme } from "../../theme/ThemeContext";
import RenewPlanModal from "./RenewPlanModal";
import { getUserEmail, getUserId } from "../../utils/auth";
import { isSubscriptionExpired } from "../../utils/subscription";

const FILTER_OPTIONS = [
  { value: "week", label: "7D" },
  { value: "month", label: "30D" },
  { value: "today", label: "Today" },
  { value: "all", label: "All" },
];

const TONE_COLOR = {
  green: "#10b981",
  yellow: "#f59e0b",
  red: "#ef4444",
  muted: "#d1d5db",
  pink: "#c084fc",
  cyan: "#22d3ee",
  orange: "#fb923c",
};

const metricBarPercent = (item) => {
  const n = Number(String(item.value ?? "").replace(/[^0-9.]/g, ""));
  if (Number.isFinite(n) && n > 0 && n <= 100) return Math.round(n);
  if (item.tone === "green") return 90;
  if (item.tone === "yellow") return 55;
  if (item.tone === "red") return 28;
  if (item.tone === "orange") return 70;
  if (item.tone === "pink" || item.tone === "cyan") return 65;
  return 12;
};

const qualityTone = (value) => {
  const v = String(value || "").trim().toUpperCase();
  if (!v || v === "—" || v === "UNKNOWN" || v === "NA" || v === "N/A") {
    return "muted";
  }
  if (v.includes("GREEN") || v === "HIGH" || v === "GOOD") return "green";
  if (v.includes("YELLOW") || v === "MEDIUM" || v === "FLAGGED") return "yellow";
  if (v.includes("RED") || v === "LOW" || v === "POOR") return "red";
  const n = Number(v);
  if (Number.isFinite(n)) {
    if (n >= 80) return "green";
    if (n >= 50) return "yellow";
    return "red";
  }
  return "muted";
};

const displayMetaValue = (value) => {
  if (value == null || value === "") return "—";
  return String(value);
};

const qualityLabel = (value) => {
  const v = String(value || "").trim().toUpperCase();
  if (!v || v === "—" || v === "UNKNOWN" || v === "NA" || v === "N/A") return "—";
  if (v.includes("GREEN") || v === "HIGH" || v === "GOOD" || v === "EXCELLENT") {
    return "Excellent";
  }
  if (v.includes("YELLOW") || v === "MEDIUM" || v === "FLAGGED") return "Medium";
  if (v.includes("RED") || v === "LOW" || v === "POOR") return "Poor";
  return displayMetaValue(value);
};

const scoreOver100 = (value) => {
  const n = Number(String(value ?? "").replace(/[^0-9.]/g, ""));
  if (Number.isFinite(n) && n > 0 && n <= 100) return `${Math.round(n)}/100`;
  return "—";
};

const formatCompactDate = (value) => {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const formatCount = (value) => {
  const n = Number(value);
  const count = Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
  return count.toLocaleString("en-IN");
};

const recoverUtf8 = (value) => {
  const text = String(value || "");
  try {
    const recoded = decodeURIComponent(
      Array.from(text, (ch) => {
        const code = ch.charCodeAt(0);
        return code <= 255
          ? `%${code.toString(16).padStart(2, "0")}`
          : encodeURIComponent(ch);
      }).join(""),
    );
    return recoded || text;
  } catch {
    return text;
  }
};

const cleanLearningTitle = (value) =>
  recoverUtf8(value)
    .replace(/^[^\p{L}\p{N}]+/u, "")
    .replace(/\s+/g, " ")
    .trim();


const firstDefined = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== "") return value;
  }
  return undefined;
};

const resolvePlanExpiry = (proj, plansRes) =>
  firstDefined(
    proj?.planExpiredOn,
    proj?.plan_expired_on,
    proj?.expiresOn,
    plansRes?.currentPlan?.expiresOn,
    plansRes?.currentPlan?.planExpiredOn,
    plansRes?.userPlanData?.expiresOn,
    plansRes?.userPlanData?.planExpiredOn,
  );

const formatOnboardedTime = (value) => {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  } catch {
    return "—";
  }
};

const toCount = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : fallback;
};

const campaignStatusLabel = (status) => {
  const s = String(status || "").trim().toUpperCase();
  if (s === "SENT" || s === "COMPLETE" || s === "COMPLETED") return "Completed";
  if (s === "SCHEDULED") return "Scheduled";
  if (
    s.includes("RUN") ||
    s.includes("SEND") ||
    s === "IN_PROGRESS" ||
    s === "PROCESSING"
  ) {
    return "Running";
  }
  return s ? String(status) : "Pending";
};

const campaignTypeLabel = (type) => {
  const t = String(type || "");
  if (/csv/i.test(t)) return "CSV Broadcast";
  if (/api/i.test(t)) return "API Campaign";
  if (/schedul/i.test(t)) return "Scheduled Broadcast";
  return t || "Campaign";
};

const campaignProgress = (row) => {
  const submitted = toCount(row?.submitteddata ?? row?.audienceCount);
  const sent = toCount(row?.sentCount);
  if (submitted > 0) return Math.min(100, Math.round((sent / submitted) * 100));
  if (campaignStatusLabel(row?.campaignstatus) === "Completed") return 100;
  return 0;
};

const normalizeAgentList = (teamRes) => {
  if (Array.isArray(teamRes)) return teamRes;
  if (Array.isArray(teamRes?.data)) return teamRes.data;
  if (Array.isArray(teamRes?.agents)) return teamRes.agents;
  return [];
};

const EMPTY_MESSAGE_TYPES = {
  marketingMessages: 0,
  authenticationMessages: 0,
  utilityMessages: 0,
  serviceMessages: 0,
};

const EMPTY_CHAT_COUNTS = {
  active: 0,
  requesting: 0,
  intervened: 0,
  total: 0,
};

const formatWaPhone = (value) => {
  if (!value) return "—";
  const text = String(value).trim();
  if (!text) return "—";
  if (text.startsWith("+")) return text;
  const digits = text.replace(/\D/g, "");
  if (!digits) return "—";
  return `+${digits}`;
};

const readWaMeta = (res) => {
  if (!res || res.success === false) return null;
  const data = res.data && typeof res.data === "object" ? res.data : res;
  if (!data || typeof data !== "object") return null;
  return data;
};

const buildAgentStats = (teamRes) => {
  const agents = normalizeAgentList(teamRes);
  const total = toCount(agents.length);
  const online = Math.min(
    toCount(
      agents.filter((agent) => agent?.isloggedIn || agent?.is_logged_in).length,
    ),
    total,
  );
  return {
    total,
    online,
    offline: Math.max(0, total - online),
  };
};

const BusinessDashboardHome = () => {
  const { message } = App.useApp();
  const { theme } = useTheme();
  const chartColor = theme.primary;
  const navigate = useNavigate();
  const { projectId, phoneNoId, phoneReady, projectLoading } = useProjectPhone();
  const { setPlanExpired } = useDashboardGate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [learning, setLearning] = useState([]);
  const [project, setProject] = useState(null);
  const [planName, setPlanName] = useState("—");
  const [dailyCampaignUsage, setDailyCampaignUsage] = useState(null);
  const [chatCounts, setChatCounts] = useState({
    active: 0,
    requesting: 0,
    intervened: 0,
    total: 0,
  });
  const [agentStats, setAgentStats] = useState({
    total: 0,
    online: 0,
    offline: 0,
  });
  const [contactStats, setContactStats] = useState({
    total: 0,
    leads: 0,
    archived: 0,
  });
  const [remainingQuota, setRemainingQuota] = useState(null);
  const [filterType, setFilterType] = useState("week");
  const [messageTypes, setMessageTypes] = useState({
    marketingMessages: 0,
    authenticationMessages: 0,
    utilityMessages: 0,
    serviceMessages: 0,
  });
  const [latestCampaign, setLatestCampaign] = useState(null);
  const [recentCampaigns, setRecentCampaigns] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [chartsLoading, setChartsLoading] = useState(true);
  const [waLinkOpen, setWaLinkOpen] = useState(false);
  const [waLinkPhone, setWaLinkPhone] = useState("");
  const [waLinkMessage, setWaLinkMessage] = useState("");
  const [waLinkCopied, setWaLinkCopied] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [planRequired, setPlanRequired] = useState(false);
  const [phoneCopied, setPhoneCopied] = useState(false);
  const [waMeta, setWaMeta] = useState(null);

  const closeUpgrade = useCallback(() => {
    if (planRequired) return;
    setUpgradeOpen(false);
  }, [planRequired]);

  const phone = useMemo(
    () => formatWaPhone(waMeta?.display_phone_number),
    [waMeta],
  );

  const onboardedAt =
    waMeta?.last_onboarded_time ||
    project?.last_onboarded_time ||
    project?.planActivatedOn ||
    project?.createdAt;

  const waLinkUrl = useMemo(() => {
    const digits = String(waLinkPhone || "").replace(/\D/g, "");
    if (!digits) return "";
    const text = waLinkMessage.trim()
      ? `?text=${encodeURIComponent(waLinkMessage.trim())}`
      : "";
    return `https://wa.me/${digits}${text}`;
  }, [waLinkPhone, waLinkMessage]);

  const openWaLinkModal = () => {
    setWaLinkPhone(phone === "—" ? "" : phone);
    setWaLinkMessage("");
    setWaLinkCopied(false);
    setWaLinkOpen(true);
  };

  const copyPhone = async () => {
    if (!phone || phone === "—") return;
    try {
      await navigator.clipboard.writeText(String(phone));
      setPhoneCopied(true);
      message.success("Phone copied");
      setTimeout(() => setPhoneCopied(false), 1500);
    } catch {
      message.error("Could not copy phone");
    }
  };

  const copyWaLink = async () => {
    if (!waLinkUrl) return;
    try {
      await navigator.clipboard.writeText(waLinkUrl);
      setWaLinkCopied(true);
      message.success("Link copied");
      setTimeout(() => setWaLinkCopied(false), 1500);
    } catch {
      message.error("Could not copy link");
    }
  };

  const loadChartStats = useCallback(async (phoneId, filter) => {
    if (!phoneId) return;
    setChartsLoading(true);
    try {
      const [campaignRes, typesRes] = await Promise.all([
        fetchLatestCampaignSummary(phoneId, filter),
        fetchSentMessagesByType(phoneId, filter),
      ]);
      setLatestCampaign(campaignRes?.data || campaignRes);
      const types = typesRes?.data || typesRes || {};
      setMessageTypes({
        marketingMessages: types.marketingMessages || 0,
        authenticationMessages: types.authenticationMessages || 0,
        utilityMessages: types.utilityMessages || 0,
        serviceMessages: types.serviceMessages || 0,
      });
    } catch {
      setLatestCampaign(null);
      setMessageTypes({ ...EMPTY_MESSAGE_TYPES });
    } finally {
      setChartsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!projectId) return undefined;
    let cancelled = false;

    setProject(null);
    setPlanName("—");
    setChatCounts({ ...EMPTY_CHAT_COUNTS });
    setAgentStats({ total: 0, online: 0, offline: 0 });
    setContactStats({ total: 0, leads: 0, archived: 0 });
    setRemainingQuota(null);
    setMessageTypes({ ...EMPTY_MESSAGE_TYPES });
    setLatestCampaign(null);
    setRecentCampaigns([]);
    setPhoneCopied(false);
    setWaMeta(null);
    setLoading(true);
    setError("");

    (async () => {
      const userId = getUserId();
      const userEmail = getUserEmail();

      try {
        const session = await loadDashboardSession({
          projectId,
          userId,
          userEmail,
        });

        if (cancelled) return;

        const proj =
          session.project && typeof session.project === "object"
            ? session.project
            : null;
        const plansRes = session.shell[6];
        setProject(proj);

        const expiry = resolvePlanExpiry(proj, plansRes);
        const hasPlanInfo = Boolean(
          proj?.planName ||
            proj?.planExpiredOn ||
            plansRes?.currentPlan ||
            plansRes?.userPlanData,
        );
        const billingExpired = hasPlanInfo && isSubscriptionExpired(expiry);
        setPlanRequired(Boolean(billingExpired));
        if (billingExpired) setUpgradeOpen(true);
        setPlanExpired(Boolean(billingExpired));

        const learningRes = session.shell[4];
        setLearning(Array.isArray(learningRes?.data) ? learningRes.data : []);

        const currentPlan =
          plansRes?.currentPlan || plansRes?.userPlanData || null;
        setPlanName(
          currentPlan?.planName || currentPlan?.name || proj?.planName || "—",
        );
        if (proj?.dailyCampaignUsage) {
          setDailyCampaignUsage(proj.dailyCampaignUsage);
        }

        const counts = session.chatCounts?.data || session.chatCounts || {};
        const totalContacts = counts.total ?? 0;
        setChatCounts({
          active: counts.active ?? 0,
          requesting: counts.requesting ?? 0,
          intervened: counts.intervened ?? 0,
          total: totalContacts,
        });
        setContactStats({
          total: totalContacts,
          leads: totalContacts,
          archived: Array.isArray(proj?.archivedContacts)
            ? proj.archivedContacts.length
            : 0,
        });

        if (session.quota?.success) {
          setRemainingQuota(session.quota.remainingQuota ?? null);
        } else if (proj?.remainingQuota != null) {
          setRemainingQuota(proj.remainingQuota);
        } else {
          setRemainingQuota(null);
        }

        apihubGet(
          `/getInteractedCustomerDetail/get-team-of-project/${projectId}`,
        )
          .then((teamRes) => {
            if (!cancelled) setAgentStats(buildAgentStats(teamRes));
          })
          .catch(() => {
            if (!cancelled) setAgentStats(buildAgentStats([]));
          });
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to load dashboard");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [projectId, setPlanExpired]);

  useEffect(() => {
    if (!projectId) {
      setDailyCampaignUsage(null);
      return undefined;
    }
    let cancelled = false;
    apihubGet(
      `/getInteractedCustomerDetail/dailycampaignusage/${encodeURIComponent(projectId)}`,
    )
      .then((res) => {
        if (cancelled) return;
        const usage =
          res?.dailyCampaignUsage ||
          res?.project?.dailyCampaignUsage ||
          res?.data?.dailyCampaignUsage ||
          null;
        if (usage) setDailyCampaignUsage(usage);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    if (!projectId || !phoneNoId || !phoneReady) {
      setWaMeta(null);
      return undefined;
    }
    let cancelled = false;
    fetchMetaPhoneStatus(phoneNoId).then((metaStatus) => {
      if (cancelled) return;
      setWaMeta(readWaMeta(metaStatus));
    });
    return () => {
      cancelled = true;
    };
  }, [projectId, phoneNoId, phoneReady]);

  useEffect(() => {
    if (!phoneNoId || !phoneReady) {
      setMessageTypes({ ...EMPTY_MESSAGE_TYPES });
      setLatestCampaign(null);
      return;
    }
    loadChartStats(phoneNoId, filterType);
  }, [phoneNoId, phoneReady, filterType, loadChartStats]);

  useEffect(() => {
    if (!phoneNoId || !phoneReady) {
      setRecentCampaigns([]);
      return undefined;
    }
    let cancelled = false;
    setRecentLoading(true);
    fetchCampaignList(phoneNoId, { page: 1, limit: 3 })
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res?.data) ? res.data.slice(0, 3) : [];
        setRecentCampaigns(rows);
      })
      .catch(() => {
        if (!cancelled) setRecentCampaigns([]);
      })
      .finally(() => {
        if (!cancelled) setRecentLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [phoneNoId, phoneReady]);

  const messageChartData = useMemo(
    () => [
      { name: "Marketing", value: messageTypes.marketingMessages },
      { name: "Auth", value: messageTypes.authenticationMessages },
      { name: "Utility", value: messageTypes.utilityMessages },
      { name: "Service", value: messageTypes.serviceMessages },
    ],
    [messageTypes],
  );

  const pageLoading = loading || projectLoading;
  const chartBusy = chartsLoading || pageLoading;
  const totalMessages = useMemo(
    () =>
      toCount(messageTypes.marketingMessages) +
      toCount(messageTypes.authenticationMessages) +
      toCount(messageTypes.utilityMessages) +
      toCount(messageTypes.serviceMessages),
    [messageTypes],
  );
  const quotaValue =
    remainingQuota != null
      ? remainingQuota
      : project?.remainingQuota != null
        ? project.remainingQuota
        : "—";
  const ratingValue = waMeta?.quality_rating || project?.quality_rating;
  const scoreValue =
    waMeta?.quality_score?.score ||
    waMeta?.quality_rating ||
    project?.quality_score?.score ||
    project?.quality_rating;
  const incomingRaw = displayMetaValue(waMeta?.incoming || project?.incoming);
  const incomingAllowed = /allow|true|yes|enabl|open/i.test(
    String(project?.incoming || ""),
  );
  const waConnected = Boolean(
    String(phoneNoId || project?.phone_number_id || "").trim(),
  );
  const waBusinessName = waMeta?.verified_name || "—";
  const waVerifyStatus = String(waMeta?.name_status || "")
    .trim()
    .toUpperCase();
  const waStatusOk = /APPROVED|VERIFIED|GREEN/.test(waVerifyStatus);
  const recentRows = recentCampaigns.length
    ? recentCampaigns
    : latestCampaign?.campaignName
      ? [latestCampaign]
      : [];
  const deliveredCount = toCount(latestCampaign?.deliveredCount);
  const failedCount = toCount(latestCampaign?.failedCount);

  const healthRows = useMemo(
    () => [
      {
        label: "Quality Rating",
        value: qualityLabel(ratingValue),
        score: scoreOver100(scoreValue),
        tone: qualityTone(ratingValue),
        kind: "bar",
        icon: FaCheckCircle,
      },
      {
        label: "Messaging Limit Tier",
        value: displayMetaValue(waMeta?.messaging_limit_tier || project?.messaging_limit_tier),
        score: "—",
        tone: "pink",
        kind: "bar",
        icon: FaBolt,
      },
      {
        label: "Quality Score",
        value:
          scoreOver100(scoreValue) === "—"
            ? displayMetaValue(scoreValue)
            : scoreOver100(scoreValue),
        score: "—",
        tone: qualityTone(scoreValue),
        kind: "bar",
        icon: FaChartLine,
      },
      {
        label: "Last Onboarded Date",
        value: formatCompactDate(onboardedAt),
        kind: "text",
        icon: FaCalendarAlt,
      },
      {
        label: "Last Onboarded Time",
        value: formatOnboardedTime(onboardedAt),
        kind: "text",
        icon: FaClock,
      },
      {
        label: "Remaining Quota",
        value: quotaValue === "—" ? "—" : formatCount(quotaValue),
        tone: "green",
        kind: "bar",
        icon: FaDatabase,
      },
      {
        label: "Incoming Message",
        value: incomingAllowed ? "Allowed" : incomingRaw,
        kind: "check",
        ok: incomingAllowed,
        icon: FaCheckCircle,
      },
    ],
    [
      ratingValue,
      scoreValue,
      project,
      onboardedAt,
      quotaValue,
      incomingAllowed,
      incomingRaw,
    ],
  );

  if (pageLoading) {
    return (
      <div className="biz-dashboard biz-dashboard-antd biz-dashboard-loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="biz-dashboard biz-dashboard-antd">
      <div className="biz-neo">
        {error ? <Alert type="error" showIcon message={error} /> : null}

        <header className="biz-dash-hero">
          <div>
            <h1>Dashboard</h1>
            <p>
              WhatsApp health, message performance, and recent campaigns.
            </p>
          </div>
        </header>

        <section className="biz-neo-kpis">
          <article className="biz-neo-kpi is-users">
            <div className="biz-neo-kpi-icon users">
              <FaUserFriends />
            </div>
            <div className="biz-neo-kpi-copy">
              <span>Total Users</span>
              <strong>{agentStats.total}</strong>
              <small>
                Online {agentStats.online} · Offline {agentStats.offline}
              </small>
            </div>
          </article>
          <article className="biz-neo-kpi is-contacts">
            <div className="biz-neo-kpi-icon contacts">
              <FaAddressBook />
            </div>
            <div className="biz-neo-kpi-copy">
              <span>Total Contacts</span>
              <strong>{contactStats.total}</strong>
              <small>
                Leads {contactStats.leads} · Archived {contactStats.archived}
              </small>
            </div>
          </article>
          <article className="biz-neo-kpi is-messages">
            <div className="biz-neo-kpi-icon messages">
              <FaComments />
            </div>
            <div className="biz-neo-kpi-copy">
              <span>Total Messages</span>
              <strong>{formatCount(totalMessages)}</strong>
              <small>
                Marketing {formatCount(messageTypes.marketingMessages)} · Service{" "}
                {formatCount(messageTypes.serviceMessages)}
              </small>
            </div>
          </article>
          <article className="biz-neo-kpi is-sessions">
            <div className="biz-neo-kpi-icon sessions">
              <FaInbox />
            </div>
            <div className="biz-neo-kpi-copy">
              <span>Chat & Sessions</span>
              <strong>{formatCount(chatCounts.active)}</strong>
              <small>
                Requested {formatCount(chatCounts.requesting)} · Intervened{" "}
                {formatCount(chatCounts.intervened)}
              </small>
            </div>
          </article>
        </section>

        <section className="biz-ga">
          <div className="biz-ga-health-col">
            <article className="biz-wa-card">
              <div className="biz-wa-card-head">
                <div>
                  <strong>{waBusinessName}</strong>
                  {waVerifyStatus ? (
                    <span
                      className={`biz-wa-status${waStatusOk ? " is-ok" : ""}`}
                    >
                      {waVerifyStatus}
                    </span>
                  ) : null}
                </div>
                <FaWhatsapp aria-hidden />
              </div>
              <button
                type="button"
                className={`biz-neo-plan-capsule biz-wa-plan-block${
                  dailyCampaignUsage &&
                  !dailyCampaignUsage.unlimited &&
                  (dailyCampaignUsage.blocked ||
                    Number(dailyCampaignUsage.remaining) <= 0)
                    ? " is-blocked"
                    : ""
                }`}
                onClick={() => setUpgradeOpen(true)}
              >
                <span className="biz-wa-plan-name">
                  {planName && planName !== "—" ? planName : "View plans"}
                </span>
                {dailyCampaignUsage ? (
                  <span className="biz-wa-plan-daily">
                    Daily{" "}
                    {dailyCampaignUsage.unlimited
                      ? `${Number(dailyCampaignUsage.sentToday || 0).toLocaleString("en-IN")} · Unlimited`
                      : `${Number(dailyCampaignUsage.sentToday || 0).toLocaleString("en-IN")} / ${Number(dailyCampaignUsage.limit || 0).toLocaleString("en-IN")}`}
                    {!dailyCampaignUsage.unlimited
                      ? ` · ${Number(
                          dailyCampaignUsage.remaining ??
                            Math.max(
                              0,
                              Number(dailyCampaignUsage.limit || 0) -
                                Number(dailyCampaignUsage.sentToday || 0),
                            ),
                        ).toLocaleString("en-IN")} left`
                      : ""}
                  </span>
                ) : null}
              </button>
              <div className="biz-wa-phone">
                <span>
                  Phone: <b>{phone}</b>
                </span>
                <button
                  type="button"
                  onClick={copyPhone}
                  aria-label="Copy phone"
                  disabled={phone === "—"}
                >
                  <FaCopy />
                </button>
              </div>
              {phoneCopied ? (
                <small className="biz-wa-copied">Copied</small>
              ) : null}
            </article>
            <article className="biz-ga-card biz-ga-health">
              <div className="biz-ga-head">
                <h2>
                  <FaWhatsapp color="#25d366" /> WhatsApp Account Health
                </h2>
                <span className={`biz-ga-connected${waConnected ? " is-on" : ""}`}>
                  {waConnected ? "Connected" : "Not connected"}
                </span>
              </div>
              {healthRows.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.label} className={`biz-ga-health-row is-${item.kind}`}>
                    <span className="biz-ga-health-icon">
                      <Icon />
                    </span>
                    <span className="biz-ga-health-label">{item.label}</span>
                    {item.kind === "check" ? (
                      <span className={`biz-ga-health-check${item.ok ? " is-ok" : ""}`}>
                        <FaCheckCircle /> {item.value}
                      </span>
                    ) : (
                      <strong className="biz-ga-health-value">{item.value}</strong>
                    )}
                    {item.kind === "bar" ? (
                      <>
                        <div className="biz-ga-health-track">
                          <span
                            style={{
                              width: `${metricBarPercent({
                                value: item.score === "—" ? item.value : item.score,
                                tone: item.tone,
                              })}%`,
                              background: TONE_COLOR[item.tone] || TONE_COLOR.muted,
                            }}
                          />
                        </div>
                        <span className="biz-ga-health-score">{item.score}</span>
                      </>
                    ) : null}
                  </div>
                );
              })}
            </article>
          </div>

          <article className="biz-ga-card biz-ga-analytics">
            <div className="biz-ga-head">
              <div>
                <h2>
                  <FaChartLine /> Message Analytics
                </h2>
                <p>Message volume & performance overview</p>
              </div>
              <div className="biz-ga-pills" role="group" aria-label="Message stats period">
                {FILTER_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`biz-ga-pill${
                      filterType === option.value ? " is-on" : ""
                    }`}
                    onClick={() => setFilterType(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <Spin spinning={chartBusy}>
              <div className="biz-ga-chart">
                <ResponsiveContainer width="100%" height={210} minWidth={0}>
                  <AreaChart
                    data={messageChartData}
                    margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="gaMsgFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={chartColor} stopOpacity={0.28} />
                        <stop offset="95%" stopColor={chartColor} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} stroke="#e8eef3" />
                    <XAxis
                      dataKey="name"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: "#5b6e80", fontFamily: "inherit" }}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      width={36}
                      tick={{ fontSize: 11, fill: "#5b6e80", fontFamily: "inherit" }}
                    />
                    <Tooltip />
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke={chartColor}
                      strokeWidth={3}
                      fill="url(#gaMsgFill)"
                      dot={{ r: 4, fill: chartColor, stroke: "#fff", strokeWidth: 2 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="biz-ga-minis">
                <div className="biz-ga-mini">
                  <span className="biz-ga-mini-icon sent" aria-hidden>
                    <FaPaperPlane />
                  </span>
                  <div className="biz-ga-mini-copy">
                    <span>Sent</span>
                    <strong>{formatCount(totalMessages)}</strong>
                  </div>
                </div>
                <div className="biz-ga-mini">
                  <span className="biz-ga-mini-icon received" aria-hidden>
                    <FaComments />
                  </span>
                  <div className="biz-ga-mini-copy">
                    <span>Delivered</span>
                    <strong>{formatCount(deliveredCount)}</strong>
                  </div>
                </div>
                <div className="biz-ga-mini">
                  <span className="biz-ga-mini-icon failed" aria-hidden>
                    <FaUserMinus />
                  </span>
                  <div className="biz-ga-mini-copy">
                    <span>Failed</span>
                    <strong>{formatCount(failedCount)}</strong>
                  </div>
                </div>
              </div>
            </Spin>
          </article>

          <div className="biz-ga-side">
            <article className="biz-ga-card biz-ga-actions">
              <h2>
                <FaBolt /> Quick actions
              </h2>
              <button
                type="button"
                className="biz-ga-action primary"
                onClick={openWaLinkModal}
              >
                <FaLink /> Create WA Link
              </button>
              <button
                type="button"
                className="biz-ga-action"
                onClick={() =>
                  navigate(businessPath(projectId, "whatsapp-widget-button"))
                }
              >
                <FaPlus /> Create WA Button
              </button>
              <button
                type="button"
                className="biz-ga-action"
                onClick={() =>
                  navigate(businessPath(projectId, "fetchalltemplates"))
                }
              >
                <FaFileAlt /> Create Template
              </button>
              <button
                type="button"
                className="biz-ga-action"
                onClick={() => navigate(businessPath(projectId, "contacts"))}
              >
                <FaUserPlus /> Add Contact
              </button>
            </article>

            <article className="biz-ga-grow">
              <FaWhatsapp />
              <div>
                <strong>Grow your business</strong>
                <p>
                  Use WhatsApp Automation to engage your customers 24/7.
                </p>
                <button
                  type="button"
                  onClick={() => navigate(businessPath(projectId, "chatbotai"))}
                >
                  Learn More →
                </button>
              </div>
            </article>
            <div className="biz-ga-hub">
              {learning.length === 0 ? (
                <article className="biz-ga-grow">
                  <FaBook />
                  <div>
                    <strong>Learning Hub</strong>
                    <p>
                      Reference guide — Template category guidelines on the
                      WABA
                    </p>
                  </div>
                </article>
              ) : (
                learning.slice(0, 3).map((item) => (
                  <article
                    className="biz-ga-grow"
                    key={item._id || item.link}
                  >
                    <FaBook />
                    <div>
                      <strong>
                        {cleanLearningTitle(item.title) || item.title}
                      </strong>
                      <a href={item.link} target="_blank" rel="noreferrer">
                        Learn More →
                      </a>
                    </div>
                  </article>
                ))
              )}
            </div>
          </div>

          <article className="biz-ga-card biz-ga-recent">
            <div className="biz-ga-head">
              <h2>
                <FaBullhorn /> Recent Campaigns
              </h2>
              <button
                type="button"
                className="biz-ga-link"
                onClick={() =>
                  navigate(businessPath(projectId, "fetchingallcampaign"))
                }
              >
                View All →
              </button>
            </div>
            <Spin spinning={recentLoading}>
              <div className="biz-ga-camp">
                <div className="biz-ga-camp-cols">
                  <span>Campaign Name</span>
                  <span>Status</span>
                  <span>Progress</span>
                  <span>Created On</span>
                  <span>Action</span>
                </div>
                {recentRows.length === 0 ? (
                  <p className="biz-ga-empty">No campaigns yet.</p>
                ) : (
                  recentRows.map((row, index) => {
                    const status = campaignStatusLabel(row.campaignstatus);
                    const progress = campaignProgress(row);
                    const tone = ["green", "blue", "purple"][index % 3];
                    return (
                      <div key={row._id || row.campaignName || index} className="biz-ga-camp-row">
                        <div className="biz-ga-camp-name">
                          <span className={`biz-ga-camp-mark ${tone}`}>
                            {/api|template/i.test(String(row.campaignType || "")) ? (
                              <FaFileAlt />
                            ) : /notif|chat|support/i.test(String(row.campaignName || "")) ? (
                              <FaComments />
                            ) : (
                              <FaBullhorn />
                            )}
                          </span>
                          <div>
                            <strong>{row.campaignName || "—"}</strong>
                            <small>{campaignTypeLabel(row.campaignType)}</small>
                          </div>
                        </div>
                        <span className={`biz-ga-status is-${status.toLowerCase()}`}>
                          {status}
                        </span>
                        <div className="biz-ga-progress">
                          <span>{progress}%</span>
                          <div className="biz-ga-progress-track">
                            <b style={{ width: `${progress}%` }} />
                          </div>
                        </div>
                        <span className="biz-ga-camp-date">
                          {formatCompactDate(row.createdAt)}
                        </span>
                        <button
                          type="button"
                          className="biz-ga-view"
                          onClick={() =>
                            navigate(
                              businessPath(projectId, `campaign/${row._id}`),
                            )
                          }
                          disabled={!row._id}
                        >
                          <FaEye /> View
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </Spin>
          </article>
        </section>
      </div>

      <RenewPlanModal
        open={upgradeOpen}
        onClose={closeUpgrade}
        projectId={projectId}
        currentPlanName={planName}
        requirePlan={planRequired}
      />

      <Modal
        title="Create WhatsApp Link"
        open={waLinkOpen}
        onCancel={() => setWaLinkOpen(false)}
        footer={[
          <Button key="close" onClick={() => setWaLinkOpen(false)}>
            Close
          </Button>,
          <Button
            key="copy"
            onClick={copyWaLink}
            disabled={!waLinkUrl}
          >
            {waLinkCopied ? "Copied!" : "Copy Link"}
          </Button>,
          <Button
            key="open"
            type="primary"
            href={waLinkUrl || undefined}
            target="_blank"
            rel="noreferrer"
            disabled={!waLinkUrl}
          >
            Open Chat
          </Button>,
        ]}
      >
        <Typography.Paragraph type="secondary">
          Share this link so customers can start a chat with a message already
          typed for them.
        </Typography.Paragraph>
        <div className="biz-neo-actions">
          <Typography.Text>WhatsApp Number (with country code)</Typography.Text>
          <Input
            value={waLinkPhone}
            onChange={(e) => setWaLinkPhone(e.target.value)}
            placeholder="919876543210"
          />
          <Typography.Text>Prefilled Message (optional)</Typography.Text>
          <Input.TextArea
            rows={3}
            value={waLinkMessage}
            onChange={(e) => setWaLinkMessage(e.target.value)}
            placeholder="Hi! I have a question"
          />
          <Typography.Text type="secondary">Generated link</Typography.Text>
          <Typography.Paragraph
            copyable={waLinkUrl ? { text: waLinkUrl } : false}
            code
          >
            {waLinkUrl || "Enter a phone number above"}
          </Typography.Paragraph>
        </div>
      </Modal>
    </div>
  );
};

export default BusinessDashboardHome;
